import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import Svg, { Path, Defs, LinearGradient, Stop } from "react-native-svg";
import NtText from "../../components/NtText";
import { useTheme } from "../../context/ThemeContext";

/**
 * Hourly sparkline of calling-card CDRs over the last 24h.
 * Buckets the cdrs array client-side by hour-of-day relative to "now".
 */
function NtCdrVolumeChart({ cdrs = [], containerStyle }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const { totals, points, pathFill, pathLine } = useMemo(() => {
    const buckets = new Array(24).fill(0);
    let answered = 0;
    let totalDuration = 0;
    let countWithDuration = 0;
    const now = Date.now();

    for (const cdr of cdrs) {
      // starttime can be ISO string or epoch
      const t = cdr?.starttime ? new Date(cdr.starttime).getTime() : null;
      if (!t || Number.isNaN(t)) continue;
      const hoursAgo = Math.floor((now - t) / (60 * 60 * 1000));
      if (hoursAgo < 0 || hoursAgo >= 24) continue;
      const bucketIdx = 23 - hoursAgo;
      buckets[bucketIdx] += 1;

      if (cdr.terminatecauseid == 16 || cdr.answered == 1) answered += 1;
      const dur = parseFloat(cdr.sessiontime || cdr.billed_sessiontime);
      if (Number.isFinite(dur) && dur > 0) {
        totalDuration += dur;
        countWithDuration += 1;
      }
    }

    const total = cdrs.length;
    const avgDurationSec =
      countWithDuration > 0 ? totalDuration / countWithDuration : 0;
    const max = Math.max(1, ...buckets);

    // Build SVG path. ViewBox 400×100.
    const W = 400;
    const H = 100;
    const pts = buckets.map((v, i) => ({
      x: (i / 23) * W,
      y: H - (v / max) * (H - 6) - 3,
    }));

    const linePath = pts
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
      .join(" ");
    const fillPath = `${linePath} L ${W} ${H} L 0 ${H} Z`;

    return {
      totals: { total, answered, avgDurationSec },
      points: pts,
      pathFill: fillPath,
      pathLine: linePath,
    };
  }, [cdrs]);

  const formatDuration = (s) => {
    if (!s || s < 0) return "0:00";
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${r}`;
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={styles.headerRow}>
        <View style={styles.statBlock}>
          <NtText style={styles.statLabel}>Total Calls</NtText>
          <NtText style={styles.statValue}>{totals.total}</NtText>
        </View>
        <View style={styles.statBlock}>
          <NtText style={styles.statLabel}>Answered</NtText>
          <NtText style={[styles.statValue, { color: theme.green }]}>
            {totals.answered}
          </NtText>
        </View>
        <View style={styles.statBlock}>
          <NtText style={styles.statLabel}>Avg Duration</NtText>
          <NtText style={[styles.statValue, { color: theme.brand2 }]}>
            {formatDuration(totals.avgDurationSec)}
          </NtText>
        </View>
      </View>

      <Svg
        viewBox="0 0 400 100"
        preserveAspectRatio="none"
        width="100%"
        height={90}
      >
        <Defs>
          <LinearGradient id="cdrFill" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0%" stopColor={theme.brand2} stopOpacity="0.3" />
            <Stop offset="100%" stopColor={theme.brand2} stopOpacity="0" />
          </LinearGradient>
        </Defs>
        <Path d={pathFill} fill="url(#cdrFill)" />
        <Path d={pathLine} fill="none" stroke={theme.brand2} strokeWidth="1.5" />
      </Svg>

      <View style={styles.axisRow}>
        <NtText style={styles.axis}>00:00</NtText>
        <NtText style={styles.axis}>06:00</NtText>
        <NtText style={styles.axis}>12:00</NtText>
        <NtText style={styles.axis}>18:00</NtText>
        <NtText style={styles.axis}>Now</NtText>
      </View>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    container: { padding: 14 },
    headerRow: { flexDirection: "row", gap: 16, marginBottom: 8 },
    statBlock: {},
    statLabel: { fontSize: 10, color: t.textMute },
    statValue: {
      fontSize: 17,
      fontWeight: "700",
      color: t.text,
      fontFamily: t.fontMono,
      fontVariant: ["tabular-nums"],
    },
    axisRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: 4,
    },
    axis: { fontSize: 10, color: t.textMute, fontFamily: t.fontMono },
  });

export default NtCdrVolumeChart;
